/**
 * Market Breadth (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_market_breadth.py).
 *
 * Na URL: período (padrão 10A, ou a data inicial no Personalizado), índice, horizonte e as condições ativas
 * (`cond=50:gte:50,200:lte:40`; vazio = nenhuma). Operador e threshold de uma métrica
 * desligada ficam só na tela, para não se perderem ao religá-la.
 */
import {
  Alert,
  Checkbox,
  Group,
  Loader,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';

import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { AvisosApi } from '../../components/AvisosApi';
import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { BarraFiltros, CampoPeriodo, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { opcoesPeriodo, type OpcaoPeriodo } from '../../lib/periodos';
import { useFiltrosUrl } from '../../lib/url-state';

// Usados enquanto a primeira resposta não chega; a API devolve a lista definitiva.
const PERIODOS = opcoesPeriodo(['1a', '2a', '5a', '10a', 'max']);

interface DadosBreadth {
  inicio: string;
  periodos: OpcaoPeriodo[];
  indices: { valor: string; rotulo: string }[];
  indice: string;
  tabela: TabelaApi;
  grafico: GraficoApi;
}

type DadosRetornos = { aviso: string } | { grafico: GraficoApi; kpis: KpiApi[] };

type Operador = 'gte' | 'lte';
interface Condicao {
  op: Operador;
  thr: number;
}

export const METRICAS = [
  { chave: '50', rotulo: '% > 50DMA' },
  { chave: '100', rotulo: '% > 100DMA' },
  { chave: '150', rotulo: '% > 150DMA' },
  { chave: '200', rotulo: '% > 200DMA' },
];
const CONDICAO_PADRAO: Condicao = { op: 'gte', thr: 50 };

/** `"50:gte:50,200:lte:40"` → `{50: {gte, 50}, 200: {lte, 40}}` (itens inválidos são ignorados). */
export function lerCondicoes(texto: string): Record<string, Condicao> {
  const resultado: Record<string, Condicao> = {};
  for (const item of texto.split(',')) {
    const [chave = '', op, thr = ''] = item.split(':');
    const numero = Number(thr);
    const valida =
      METRICAS.some((m) => m.chave === chave) &&
      (op === 'gte' || op === 'lte') &&
      thr !== '' &&
      numero >= 0 &&
      numero <= 100;
    if (valida) resultado[chave] = { op, thr: numero };
  }
  return resultado;
}

/** Inverso de `lerCondicoes`, sempre na ordem das métricas. */
export function escreverCondicoes(condicoes: Record<string, Condicao>): string {
  return METRICAS.flatMap((m) => {
    const c = condicoes[m.chave];
    return c ? [`${m.chave}:${c.op}:${c.thr}`] : [];
  }).join(',');
}

function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

export function MarketBreadth() {
  const [filtros, definir] = useFiltrosUrl({
    periodo: '10a',
    inicio: '',
    indice: '',
    horizonte: 21,
    cond: '50:gte:50',
  });
  const ativas = lerCondicoes(filtros.cond);
  const [inativas, setInativas] = useState<Record<string, Condicao>>({});
  const condicao = (chave: string) => ativas[chave] ?? inativas[chave] ?? CONDICAO_PADRAO;

  const pagina = useQuery({
    queryKey: ['equities.market_breadth', filtros.periodo, filtros.inicio, filtros.indice],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosBreadth>>(
        '/equities/market-breadth',
        { periodo: filtros.periodo, inicio: filtros.inicio, indice: filtros.indice || undefined },
        signal,
      ),
    placeholderData: keepPreviousData,
  });
  const dados = pagina.data?.data;
  // Sem índice na URL, o backend escolhe o primeiro da tabela (como o legado).
  const indice = filtros.indice || dados?.indice;

  const condicoesApi = escreverCondicoes(ativas).split(',').filter(Boolean);
  const retornos = useQuery({
    queryKey: [
      'equities.market_breadth.retornos',
      filtros.periodo,
      filtros.inicio,
      indice,
      filtros.horizonte,
      condicoesApi,
    ],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosRetornos>>(
        '/equities/market-breadth/retornos',
        {
          periodo: filtros.periodo,
          inicio: filtros.inicio,
          indice,
          horizonte: filtros.horizonte,
          cond: condicoesApi,
        },
        signal,
      ),
    enabled: !!indice && condicoesApi.length > 0,
    placeholderData: keepPreviousData,
  });

  const mudarCondicao = (chave: string, mudanca: Partial<Condicao>) => {
    const nova = { ...condicao(chave), ...mudanca };
    if (ativas[chave]) definir({ cond: escreverCondicoes({ ...ativas, [chave]: nova }) });
    else setInativas((atual) => ({ ...atual, [chave]: nova }));
  };
  const ligar = (chave: string, ligado: boolean) => {
    const proximas = { ...ativas };
    if (ligado) proximas[chave] = condicao(chave);
    else {
      setInativas((atual) => ({ ...atual, [chave]: condicao(chave) }));
      delete proximas[chave];
    }
    definir({ cond: escreverCondicoes(proximas) });
  };

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Market Breadth"
        descricao="Percentual de ações acima das médias móveis em cada índice e os retornos futuros nos dias em que as condições de breadth foram atendidas."
        dadosAte={pagina.data?.meta.dados_ate}
        fonte="Banco Persevera (indicadores)"
      />
      <BarraFiltros carregando={pagina.isFetching || retornos.isFetching}>
        <GrupoFiltros titulo="Período">
          <CampoPeriodo
            periodo={filtros.periodo}
            inicio={filtros.inicio}
            inicioEfetivo={dados?.inicio}
            opcoes={dados?.periodos ?? PERIODOS}
            min="2010-01-01"
            aoMudar={definir}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Gráfico de breadth">
          <Select
            label="Índice"
            searchable
            allowDeselect={false}
            disabled={!dados}
            data={(dados?.indices ?? []).map((i) => ({ value: i.valor, label: i.rotulo }))}
            value={indice ?? null}
            onChange={(valor) => valor && definir({ indice: valor })}
            w={LARGURA.selecao}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Retornos condicionados">
          <NumberInput
            label="Horizonte (dias)"
            min={1}
            max={252}
            value={filtros.horizonte}
            onChange={(v) =>
              typeof v === 'number' && v >= 1 && v <= 252 && definir({ horizonte: v })
            }
            w={LARGURA.numero}
          />
          {METRICAS.map((m) => {
            const c = condicao(m.chave);
            return (
              <Stack key={m.chave} gap={4}>
                <Checkbox
                  label={m.rotulo}
                  checked={!!ativas[m.chave]}
                  onChange={(e) => ligar(m.chave, e.currentTarget.checked)}
                  size="sm"
                />
                <Group gap={4} wrap="nowrap">
                  <SegmentedControl
                    size="xs"
                    aria-label={`Condição ${m.rotulo}`}
                    value={c.op}
                    onChange={(op) => mudarCondicao(m.chave, { op: op as Operador })}
                    data={[
                      { value: 'gte', label: '≥' },
                      { value: 'lte', label: '≤' },
                    ]}
                  />
                  <NumberInput
                    size="xs"
                    aria-label={`Threshold ${m.rotulo}`}
                    min={0}
                    max={100}
                    step={5}
                    suffix="%"
                    value={c.thr}
                    onChange={(v) =>
                      typeof v === 'number' &&
                      v >= 0 &&
                      v <= 100 &&
                      mudarCondicao(m.chave, { thr: v })
                    }
                    w={72}
                  />
                </Group>
              </Stack>
            );
          })}
        </GrupoFiltros>
      </BarraFiltros>

      {pagina.isError && (
        <Alert color="red">{mensagem(pagina.error, 'Não foi possível carregar os dados.')}</Alert>
      )}
      {pagina.isPending && <Loader />}

      {dados && (
        <>
          <AvisosApi avisos={[pagina.data?.meta.avisos, retornos.data?.meta.avisos]} />
          <Secao
            titulo="Breadth por índice"
            legenda="Último fechamento e % de membros acima das médias de 50, 100, 150 e 200 dias."
          >
            <TabelaDados tabela={dados.tabela} alturaAutomatica />
          </Secao>

          <Secao
            titulo="Histórico do índice escolhido"
            legenda="Fechamento (eixo esquerdo) e % de membros acima de cada média móvel (eixo direito)."
          >
            <Grafico grafico={dados.grafico} />
          </Secao>

          <Secao
            titulo="Retornos condicionados"
            legenda="Distribuição dos retornos futuros nos dias em que todas as condições ativas valeram ao mesmo tempo."
          >
            <Retornos semCondicao={condicoesApi.length === 0} consulta={retornos} />
          </Secao>
        </>
      )}
    </Pagina>
  );
}

function Retornos({
  semCondicao,
  consulta,
}: {
  semCondicao: boolean;
  consulta: UseQueryResult<Envelope<DadosRetornos>>;
}) {
  if (semCondicao) return <Alert color="yellow">Ative pelo menos um indicador de breadth.</Alert>;
  if (consulta.isError)
    return <Alert color="red">{mensagem(consulta.error, 'Erro ao calcular os retornos.')}</Alert>;
  const dados = consulta.data?.data;
  if (!dados) return <Loader />;
  if ('aviso' in dados) return <Alert color="yellow">{dados.aviso}</Alert>;
  return (
    <Stack>
      <Grafico grafico={dados.grafico} />
      <SimpleGrid cols={{ base: 2, md: 4 }}>
        {dados.kpis.map((k) => (
          <Kpi key={k.rotulo} kpi={k} />
        ))}
      </SimpleGrid>
      <Text size="xs" c="dimmed">
        Cada observação é o retorno do índice nos pregões seguintes a um dia em que todas as
        condições ativas foram verdadeiras.
      </Text>
    </Stack>
  );
}
